import {
  AfterViewInit,
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { SharedService } from '../../services/shared.service';
import Swiper from 'swiper';
import { Navigation, Autoplay, Pagination } from 'swiper/modules';

@Component({
  selector: 'app-my-carousel-swiper',
  templateUrl: './my-carousel-swiper.component.html',
  styleUrls: ['./my-carousel-swiper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyCarouselSwiperComponent
  implements AfterViewInit, OnChanges, AfterViewChecked, OnDestroy
{
  @Input() config = null;
  @Input() navigation = true;
  @Input() navigationDesign: 'default' | 'arrows-right' = 'default';
  @Input() slide: any[] = [];
  @Input() centerSlider = false;
  @Input() cssClass: string | null = null;
  @Input() autoplay: boolean = false;
  @Input() isStatic: boolean = false; //rebuilding when not needed temporary fix for some components
  @Output() onSwiper = new EventEmitter<any>();
  @Output() onSlideChange = new EventEmitter<any>();

  @ViewChild('swiperContainer') swiperContainer!: ElementRef;

  private swiper: Swiper | null = null;
  private lastSlideCount = 0;
  private rebuildPending = false;

  constructor(private sharedService: SharedService) {}

  ngAfterViewInit(): void {
    if (this.sharedService.isBrowser()) {
      this.initSwiper();
    }
  }

  ngAfterViewChecked(): void {
    if (this.rebuildPending || this.isStatic) return;

    const swiperSlides = this.getSwiperSlides();
    const slidesLen = swiperSlides?.length;

    if (slidesLen !== this.lastSlideCount && slidesLen > 0) {
      this.rebuildPending = true;
      this.lastSlideCount = slidesLen;

      Promise.resolve().then(() => {
        this.rebuildSwiper();
        this.rebuildPending = false;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['slide'] && this.slide?.length > 0) {
      const prevLen = changes['slide'].previousValue?.length ?? 0;
      const currLen = this.slide?.length;

      if (currLen !== prevLen) {
        this.rebuildSwiper();
      }
    }

    if (changes['config'] && !changes['config'].firstChange) {
      this.rebuildSwiper();
    }
  }

  ngOnDestroy(): void {
    this.destroySwiper();
  }

  private initSwiper(): void {
    if (!this.swiperContainer?.nativeElement) return;

    const defaultConfig: any = {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: 1,
      spaceBetween: 10,
      pagination: {
        el: '.my-swiper-pagination',
        clickable: true,
      },
      navigation: this.navigation
        ? {
            nextEl: '.my-swiper-next',
            prevEl: '.my-swiper-prev',
          }
        : false,
      autoplay: this.autoplay
        ? {
            delay: 3000,
            disableOnInteraction: false,
          }
        : false,
    };

    const finalConfig = { ...defaultConfig, ...this.config };

    this.swiper = new Swiper(this.swiperContainer.nativeElement, finalConfig);

    this.onSwiper.emit(this.swiper);
    this.swiper.on('slideChange', (swiper: Swiper) => {
      this.onSlideChange.emit(swiper);
    });
    if (this.centerSlider) this.adjustContainerWidth();
  }

  private destroySwiper(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  private rebuildSwiper(): void {
    this.destroySwiper();

    if (this.sharedService.isBrowser()) {
      this.initSwiper();
    }
  }

  private adjustContainerWidth(): void {
    if (!this.swiperContainer?.nativeElement || !this.swiper) return;

    const wrapper =
      this.swiperContainer.nativeElement.querySelector('.swiper-wrapper');
    if (!wrapper || !this.slide?.length) return;

    const totalWidth = Array.from(wrapper.children).reduce(
      (sum: number, el: any) => {
        return sum + el.offsetWidth + (this.config?.spaceBetween || 0);
      },
      0
    );

    const containerWidth = this.swiperContainer.nativeElement.offsetWidth;
    wrapper.style.justifyContent = totalWidth < containerWidth ? 'center' : '';

    this.swiper.update();
  }

  private getSwiperSlides(): Element[] {
    return Array.from(
      this.swiperContainer?.nativeElement?.querySelectorAll('.swiper-slide') ??
        []
    );
  }

  public getSwiperInstance(): Swiper | null {
    return this.swiper;
  }
}
